/* eslint-disable import/extensions */
import { ActionCancel } from '@/components/action-button/cancel';
import ActionPrint from '@/components/action-button/print';
import BaseList from '@/components/base-list';
import { LoadingOverlay } from '@/components/loading-overlay/loading-overlay';
import { useCanPerform } from '@/hooks/useCanPerform';
import { useModal } from '@/hooks/useModal';
import { useOrder } from '@/modules/order/application/order';
import { convertNumberToCurrency } from '@/utility/helper/common';
import buildFilterPayload from '@/utility/helper/filterbuilder';
import { useInvalidate, useNavigation } from '@refinedev/core';
import dayjs from 'dayjs';
import {
  Modal, ModalContentBody, ModalContentHead, ModalContentWrapper, ModalFooter, useSnackbar,
} from '@rrexpress/core';
import { get, isEmpty } from 'lodash';
import {
  useEffect, useMemo, useRef, useState,
} from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useReactToPrint } from 'react-to-print';
import { CancelConfirmation } from './components/cancel-confirmation';
import ImportOrder from './import/import-order';
import OrderPrintAwb from './shipping-label/print-awb';

export function OrderListPage() {
  const [isActionLoading, setIsActionLoading] = useState(false);
  const [isModalLoading, setIsModalLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [orderData, setOrderData] = useState<any>([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const queryParams = Object.fromEntries([...searchParams]);

  const {
    handleCancelOrder,
    handleExportOrder,
    handleImportOrder,
    getAwbData,
  } = useOrder();

  const { create } = useNavigation();
  const { show } = useSnackbar();
  const invalidate = useInvalidate();
  const modal = useModal();

  const canPerform = useCanPerform('orders');

  const componentToPrintOrder = useRef(null);
  const handlePrintOrder = useReactToPrint({
    documentTitle: 'Print this Document',
    contentRef: componentToPrintOrder,
    pageStyle: `
    @page {
     size: 100mm 100mm !important;
    }
    .print-container {
      font-family: sans-serif !important;
      font-weight: bold !important;
      color: black;
    }
   `,
  });

  function onDeleteOrder(order: any) {
    const payload: CancelOrderPayload = {
      order_id: order.order_id,
      row_version: order.row_version,
    };

    setIsActionLoading(true);
    handleCancelOrder(payload)
      .then(() => {
        show('Sukses cancel order', 'success');
        invalidate({
          resource: 'orders',
          invalidates: ['list'],
          dataProviderName: 'default',
        });
      })
      .catch((err) => {
        console.error(err);
        show(`Error - Gagal cancel order | error message: ${err.message}`, 'error');
      })
      .finally(() => {
        setIsActionLoading(false);
        modal.close();
      });
  }

  function onPrint(awb: string) {
    setIsActionLoading(true);
    getAwbData(awb)
      .then((res) => {
        setOrderData(res);
      })
      .catch((err) => {
        console.error(err);
        show(`Error - Gagal mendapatkan data Resi | error message: ${err?.message}`, 'error');
      })
      .finally(() => setIsActionLoading(false));
  }

  function importRoute(file: any) {
    setIsModalLoading(true);
    handleImportOrder(file)
      .then((res: any) => {
        if (res) {
          show('Berhasil Import data', 'success');
          setIsModalOpen(false);
        }
      })
      .catch((err: any) => {
        show(`${err}`, 'error');
      })
      .finally(() => setIsModalLoading(false));
  }

  async function onExport() {
    try {
      const payload = buildFilterPayload(queryParams);
      delete payload.filter.forceFetchKey;
      payload.pagination = {
        limit: 0,
        offset: 0,
        page: 0,
      };

      const response = await handleExportOrder(payload);
      const disposition = response.headers['content-disposition'];
      let filename = `order-list-${dayjs(new Date()).unix()}`;

      if (disposition && disposition.indexOf('filename=') !== -1) {
        const matches = /filename="?([^"]+)"?/.exec(disposition);
        if (matches?.[1]) {
          filename = matches?.[1];
        }
      }

      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error: any) {
      show(error.message, 'error');
    }
  }

  const columns: any[] = useMemo(() => [
    {
      id: 'awbNumber',
      accessorKey: 'awb_number',
      header: 'No Resi',
      cell: ({ row }: any) => (
        <Link to={`/orders/show/${row.original.order_id}`}>
          <div className="text-blue-500">{row.original.awb_number}</div>
        </Link>
      ),
    },
    {
      id: 'orderNumber',
      accessorKey: 'order_number',
      header: 'No Order',
      cell: ({ row }: any) => (
        <Link to={`/orders/show/${row.original.order_id}`}>
          <span className="text-blue-500">{row.original.order_number}</span>
        </Link>
      ),
    },
    {
      id: 'orderDate',
      accessorKey: 'order_date_time_utc',
      header: 'Tgl Order',
      cell: ({ getValue }: any) => dayjs(getValue() as string).format('DD/MM/YYYY'),
    },
    {
      id: 'branch',
      accessorKey: 'branch_name',
      header: 'Cabang',
    },
    {
      id: 'sender',
      accessorKey: 'shipper_name',
      header: 'Pengirim',
    },
    {
      id: 'receiver',
      accessorKey: 'consignee_name',
      header: 'Penerima',
    },
    {
      id: 'originCity',
      accessorKey: 'origin_name',
      header: 'Kota Asal',
    },
    {
      id: 'destinationCity',
      accessorKey: 'consignee_delivery_city_name',
      header: 'Kota Tujuan',
    },
    {
      id: 'weight',
      accessorKey: 'parcel_weight',
      header: <div className="text-right">Berat</div>,
      cell: ({ getValue }: any) => <div className="text-right">{`${getValue()} Kg`}</div>,
    },
    {
      id: 'totalCost',
      accessorKey: 'total_price',
      header: <div className="text-right">Total Biaya Kirim</div>,
      cell: ({ getValue }: any) => <div className="text-right">{convertNumberToCurrency((getValue() as number) || 0)}</div>,
    },
    {
      id: 'service',
      accessorKey: 'product_service_code',
      header: 'Layanan',
    },
    {
      id: 'lastStatus',
      accessorKey: 'awb_status_name',
      header: 'Status Akhir',
    },
    {
      id: 'lastStatusDate',
      accessorKey: 'updated_time_utc',
      header: 'Tgl Status Akhir',
      cell: ({ getValue }: any) => dayjs(getValue() as string).format('DD/MM/YYYY'),
    },
    {
      id: 'action',
      accessorKey: '',
      header: 'Action',
      cell: ({ row: { original } }) => (
        <LoadingOverlay
          isLoading={isActionLoading}
        >
          <div className="flex items-center gap-2">
            <ActionPrint onClick={() => onPrint(original?.awb_number)} />
            <ActionCancel
              onClick={() => modal.open({
                template: <CancelConfirmation
                  onCancel={() => modal.close()}
                  onSubmit={() => onDeleteOrder(original)}
                />,
                title: 'Konfirmasi',
                footer: <></>,
              })}
            />
          </div>
        </LoadingOverlay>
      ),
    },
  ], [isActionLoading]);

  useEffect(() => {
    if (!isEmpty(orderData)) {
      handlePrintOrder();
    }
  }, [orderData]);

  return (
    <>
      <Modal width={1000} open={isModalOpen} onClose={() => setIsModalOpen(false)}>
        <ModalContentWrapper>
          <ModalContentHead
            title="Import"
            onClose={() => {
              setIsModalOpen(false);
            }}
          />
          <ModalContentBody>
            <LoadingOverlay isLoading={isModalLoading}>
              <ImportOrder onUpload={importRoute} />
            </LoadingOverlay>
          </ModalContentBody>
        </ModalContentWrapper>
      </Modal>

      <div className="hidden drop-print spave-y-2 px-5 text-[10px]">
        <OrderPrintAwb
          ref={componentToPrintOrder}
          airwaybill={orderData}
          number={0}
          qty={1}
        />
      </div>

      <BaseList
        resource="orders"
        dataProviderName="default"
        initialSorters={[
          {
            field: 'order_date_time_utc',
            order: 'desc',
          },
        ]}
        headerActions={[
          {
            type: 'tertiary',
            label: 'Import',
            onClick: () => setIsModalOpen(true),
          },
          {
            type: 'tertiary',
            label: 'Export',
            onClick: () => onExport(),
          },
          {
            type: 'tertiary',
            label: 'Tambah Baru',
            onClick: () => {
              if (canPerform.create) create('orders');
            },
          },
        ]}
        filter={{
          onSubmit: (data) => {
            const filter = { ...data };

            if (get(filter, 'order_date_time_utc_end')) filter.order_date_time_utc_end = dayjs(filter.order_date_time_utc_end).add(1, 'day').toISOString();
            setSearchParams(data);

            return filter;
          },
          sections: [
            {
              layout: {
                direction: 'vertical',
                sections: [
                  {
                    layout: {
                      direction: 'horizontal',
                      sections: [
                        {
                          input: {
                            type: 'datepicker',
                            label: 'Dari Tgl Order',
                            name: 'order_date_time_utc_start',
                          },
                        },
                        {
                          input: {
                            type: 'datepicker',
                            label: 'Sampai Tgl Order',
                            name: 'order_date_time_utc_end',
                          },
                        },
                        {
                          input: {
                            type: 'queryfield',
                            name: 'branch_id',
                            label: 'Cabang',
                            comboboxResource: 'branches',
                            queryfieldKeyOptionLabel: 'branch_name',
                            queryfieldKeyOptionValue: 'branch_id',
                            queryfieldKeyQueryFilter: 'branch_name',
                          },
                        },
                      ],
                    },
                  },
                  {
                    layout: {
                      direction: 'horizontal',
                      sections: [
                        {
                          input: {
                            type: 'textfield',
                            label: 'No Resi',
                            name: 'awb_number',
                          },
                        },
                        {
                          input: {
                            type: 'textfield',
                            label: 'Pengirim',
                            name: 'shipper_name',
                          },
                        },
                        {
                          input: {
                            type: 'textfield',
                            label: 'Penerima',
                            name: 'consignee_name',
                          },
                        },
                      ],
                    },
                  },
                  {
                    layout: {
                      direction: 'horizontal',
                      sections: [
                        {
                          input: {
                            type: 'queryfield',
                            name: 'shipper_pickup_city_id',
                            label: 'Kota Asal',
                            comboboxResource: 'cities',
                            queryfieldKeyOptionLabel: 'city_name',
                            queryfieldKeyOptionValue: 'city_id',
                            queryfieldKeyQueryFilter: 'city_name',
                            dataProviderName: 'masterdata',
                          },
                        },
                        {
                          input: {
                            type: 'queryfield',
                            name: 'consignee_delivery_city_id',
                            label: 'Kota Tujuan',
                            comboboxResource: 'cities',
                            queryfieldKeyOptionLabel: 'city_name',
                            queryfieldKeyOptionValue: 'city_id',
                            queryfieldKeyQueryFilter: 'city_name',
                            dataProviderName: 'masterdata',
                          },
                        },
                        {
                          input: {
                            type: 'textfield',
                            label: 'Status',
                            name: 'awb_status_name',
                          },
                        },
                      ],
                    },
                  },
                ],
              },
            },
          ],
        }}
        columns={columns as any}
      />
    </>
  );
}

export default OrderListPage;
